import type { Form } from "../../../components/form/form";
import type {
  ObPaymentPageViewFormType,
  ObPaymentPageViewStore,
} from "../../obPaymentPage/obPaymentPageView/obPaymentPageViewStore";
import type { PayAttempt, PayProgress } from "./payProgress";

/**
 * The attempt behind one pay-button press: runs the store's transaction, then reports whether the
 * page is about to navigate away (the store set a redirect). A thrown transaction propagates, and
 * `PayProgress` ends the attempt.
 */
export const payAttempt =
  (store: Pick<ObPaymentPageViewStore, "redirectUrl">, transaction: () => Promise<void>): PayAttempt =>
  async () => {
    await transaction();
    return store.redirectUrl !== null;
  };

export type PayPress = {
  /** The live `isPayFormValid` result the buttons are disabled by */
  formValid: boolean;
  form: Pick<Form<ObPaymentPageViewFormType>, "isValid">;
  progress: PayProgress;
  /** Sends the press's analytics event (`gaSendUserEvent` with the button's event name) */
  track: () => void;
  attempt: PayAttempt;
};

/**
 * A pay-button press. It runs only on a valid form and outside a running attempt (as today:
 * `form.isValid()` in the handler, checked last because it writes the field errors), and only such
 * a press sends its analytics event. Returns the running attempt, or `null` for an ignored press.
 */
export function pressPay({ formValid, form, progress, track, attempt }: PayPress): Promise<void> | null {
  if (!formValid || progress.inProgress || !form.isValid()) {
    return null;
  }
  track();
  return progress.run(attempt);
}
